import { Badge } from '@/admin/components/ui/badge';
import { Button } from '@/admin/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/admin/components/ui/card';
import type { AdminChatTraceResponse } from '../../api';
import { formatDateTime, formatMs, statusVariant } from '../../format';
import { parseTracePayload } from '../../trace/parse-payload';
import type { TracePresentationEvent } from '../../trace/presentation';
import { JsonBlock } from '../raw/raw-tab';
import { CopyId } from '../trace/copy-id';
import { EventDetailContent } from './event-cards';

export type DetailSubTab = 'overview' | 'raw';

export function DetailPanel({
  item,
  diagnostics,
  detailTab,
  onDetailTab,
}: {
  item: TracePresentationEvent | null;
  diagnostics: AdminChatTraceResponse['diagnostics'];
  detailTab: DetailSubTab;
  onDetailTab: (tab: DetailSubTab) => void;
}) {
  const event = item?.event || null;
  const eventDiagnostics = diagnostics.filter(diagnostic =>
    diagnostic.affected_event_ids?.includes(event?.id || '')
  );
  const tabs: DetailSubTab[] = ['overview', 'raw'];
  const activeTab = tabs.includes(detailTab) ? detailTab : 'overview';
  return (
    <Card className="min-h-0 xl:flex xl:flex-col">
      <CardHeader className="xl:shrink-0">
        <CardTitle className="text-base">Detail</CardTitle>
      </CardHeader>
      <CardContent className="xl:min-h-0 xl:flex-1 xl:overflow-auto">
        {!event ? (
          <div className="text-sm text-muted-foreground">Select an event.</div>
        ) : (
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between gap-2">
                <div className="font-medium">{event.title || event.kind}</div>
                <Badge variant={statusVariant(event.status)}>
                  {event.status}
                </Badge>
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                <CopyId value={event.id} label="event id" /> · request{' '}
                <CopyId value={event.request_id} label="request id" />
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2">
                {tabs.map(tab => (
                  <Button
                    key={tab}
                    type="button"
                    size="sm"
                    variant={activeTab === tab ? 'default' : 'outline'}
                    onClick={() => onDetailTab(tab)}
                  >
                    {tab[0].toUpperCase() + tab.slice(1)}
                  </Button>
                ))}
              </div>
              {activeTab === 'overview' ? (
                <>
                  <dl className="space-y-2 text-sm">
                    <DetailRow label="Kind" value={event.kind} />
                    <DetailRow label="At" value={formatDateTime(event.at)} />
                    <DetailRow
                      label="Duration"
                      value={formatMs(event.duration_ms)}
                    />
                    <DetailRow
                      label="Quality"
                      value={event.quality}
                      hideExact
                    />
                  </dl>
                  <EventDetailContent
                    event={event}
                    parsed={item?.parsed || parseTracePayload(event)}
                  />
                  {eventDiagnostics.length > 0 ? (
                    <div className="mt-4 space-y-2">
                      {eventDiagnostics.map(diagnostic => (
                        <div
                          key={diagnostic.id}
                          className="rounded-md border border-destructive/40 p-2 text-sm"
                        >
                          <div className="font-medium">{diagnostic.title}</div>
                          <div className="text-muted-foreground">
                            {diagnostic.detail || '-'}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </>
              ) : null}
              {activeTab === 'raw' ? (
                <JsonBlock
                  value={{
                    raw_ref: event.raw_ref,
                    payload: event.payload,
                    parsed: item?.parsed,
                    row: event,
                  }}
                />
              ) : null}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DetailRow({
  label,
  value,
  hideExact,
  multiline,
}: {
  label: string;
  value?: string;
  hideExact?: boolean;
  multiline?: boolean;
}) {
  if (hideExact && value === 'exact') return null;
  return (
    <div className="grid grid-cols-[90px_minmax(0,1fr)] gap-2">
      <dt className="text-muted-foreground">{label}</dt>
      <dd
        className={
          multiline
            ? 'max-h-72 overflow-auto whitespace-pre-wrap break-words rounded-md bg-muted/40 p-2'
            : 'break-words'
        }
      >
        {value || '-'}
      </dd>
    </div>
  );
}
